import { useState } from 'react';
import type { RunSummary } from '@ni/shared';
import { useCreateEventRun, useEventRunStateInvalidation } from '../../api/event-hooks';
import { useCreditPrice, useCreditShortfall, useRun } from '../../api/hooks';
import { Button } from '../../components/ui/button';
import { ErrorLine } from '../../components/ui/error-line';
import { Modal } from '../../components/ui/modal';
import { WorkingButton } from '../../components/ui/working-button';
import {
  EVENT_RESEARCH_AGAIN,
  EVENT_RESEARCH_CANCEL,
  EVENT_RESEARCH_CONFIRM_LINE,
  EVENT_RESEARCH_START,
  EVENT_RESEARCH_STARTING,
  eventResearchFailedLine,
  pricedLabel,
} from '../../lib/copy';
import { isOfficeEndedRun, latestRunId, organisationRunActive } from '../../lib/run-state';

/**
 * **Where the event's research stands**, read the same way by the top bar's
 * button and the Overview's run strip: the run going (one just started counts
 * before the record lists it), whether research ever finished, and the failure
 * line for a failed run the office did not end itself.
 */
export function useEventResearch(all: RunSummary[], startedRunId: string | null) {
  /* The record lists every run on the event, its attendee scans too; a scan is
     the Analysis tab's, never this research. */
  const runs = all.filter((run) => run.kind === 'event_research');
  const started = runs.some((run) => run.id === startedRunId) ? null : startedRunId;
  const runQuery = useRun(started ?? latestRunId(runs));
  useEventRunStateInvalidation(runQuery.data);
  const active = organisationRunActive(runs, runQuery.data?.state ?? null, started);
  // Research that found nothing was not charged, and is not when the event was researched.
  const done = runs.filter((run) => run.state === 'complete' && run.delivered !== false);
  const latest = runQuery.data ?? runs[0] ?? null;
  const failure =
    !active && latest?.state === 'failed' && !isOfficeEndedRun(latest)
      ? eventResearchFailedLine(latest.error?.message ?? null)
      : null;
  return {
    runQuery,
    active,
    /** The latest research run's state, or null when research never ran. */
    latestState: latest?.state ?? null,
    /** The latest run finished with nothing found. */
    latestEmpty: latest?.state === 'complete' && latest.delivered === false,
    everCompleted: done.length > 0,
    /** When the latest research finished. */
    lastCompleteAt: done[0] ? (done[0].completed_at ?? done[0].created_at) : null,
    failure,
  };
}

/**
 * **Start research, or research again**, behind its confirmation: it spends,
 * and the confirmation's button carries the price.
 */
export function EventResearchAction({
  entityId,
  again,
  onStarted,
}: {
  entityId: string;
  again: boolean;
  onStarted: (runId: string) => void;
}) {
  const [asking, setAsking] = useState(false);
  const start = useCreateEventRun(entityId);
  const price = useCreditPrice('event_research');
  const short = useCreditShortfall(price.units);
  const label = again ? EVENT_RESEARCH_AGAIN : EVENT_RESEARCH_START;
  return (
    <>
      <Button variant={again ? 'act' : 'go'} onClick={() => setAsking(true)}>
        {label}
      </Button>
      {asking ? (
        <Modal label={label} narrow onClose={() => setAsking(false)}>
          <div className="modal-head">{label}</div>
          <div className="modal-line">{EVENT_RESEARCH_CONFIRM_LINE}</div>
          {start.isError ? <ErrorLine error={start.error} /> : null}
          <div className="modal-actions">
            <WorkingButton
              label={pricedLabel(label, price.units, price.unitsPerCredit)}
              workingLabel={EVENT_RESEARCH_STARTING}
              working={start.isPending}
              disabled={short !== null}
              onClick={() =>
                start.mutate(price.units, {
                  onSuccess: (run) => {
                    onStarted(run.id);
                    setAsking(false);
                  },
                })
              }
            />
            {short ? <span className="credit-short">{short}</span> : null}
            <Button variant="act" onClick={() => setAsking(false)}>
              {EVENT_RESEARCH_CANCEL}
            </Button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
